import React, { useId } from 'react'
import { CloseIcon, DeleteIcon, EditIcon } from '@/assets/icons'
import { Button } from '@components/Button'
import { useOverlayDismiss } from '@hooks'
import {
	CloseButton,
	DeleteButton,
	ModalBackdrop,
	ModalBody,
	ModalContainer,
	ModalFooter,
	ModalHeader,
	ModalHeaderActions,
	ModalTitle,
	type ModalSize,
} from './Modal.styles'

export { DeleteButton } from './Modal.styles'
import { iconSizes } from '@/theme/tokens'

interface ModalProps {
	isOpen: boolean
	onClose: () => void
	title: string
	children: React.ReactNode
	footer?: React.ReactNode
	showCloseButton?: boolean
	size?: ModalSize
	closeOnBackdropClick?: boolean
	backgroundColor?: string
	/** Puts a pencil in the header, to the left of the trash when there is one. */
	onEdit?: () => void
	onDelete?: () => void
	/** The dialog's e2e test ID, for a dialog that stands for a screen (see `@/testIds`). */
	testId?: string
	/** The header delete button's e2e test ID. */
	deleteTestId?: string
}

/**
 * The app's one dialog chrome (ADR 0008): warm scrim, 16px card radius, overlay
 * shadow, Lato title with no header divider. Owns Escape-to-close and the body
 * scroll lock; pages supply title, body and an optional footer.
 */
const Modal: React.FC<ModalProps> = ({
	isOpen,
	onClose,
	title,
	children,
	footer,
	showCloseButton = true,
	size = 'medium',
	closeOnBackdropClick = true,
	backgroundColor,
	onEdit,
	onDelete,
	testId,
	deleteTestId,
}) => {
	useOverlayDismiss(isOpen, onClose)
	const titleId = useId()

	const handleBackdropClick = (e: React.MouseEvent) => {
		if (closeOnBackdropClick && e.target === e.currentTarget) {
			onClose()
		}
	}

	if (!isOpen) return null

	return (
		<ModalBackdrop onClick={handleBackdropClick}>
			<ModalContainer
				$size={size}
				$backgroundColor={backgroundColor}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				data-testid={testId}
			>
				<ModalHeader>
					<ModalTitle id={titleId}>{title}</ModalTitle>
					<ModalHeaderActions>
						{onEdit && (
							<Button type="button" variant="icon" onClick={onEdit} title="Edit" aria-label="Edit">
								<EditIcon sx={{ fontSize: iconSizes.lg }} />
							</Button>
						)}
						{onDelete && (
							<DeleteButton type="button" onClick={onDelete} title="Delete" aria-label="Delete" data-testid={deleteTestId}>
								<DeleteIcon sx={{ fontSize: iconSizes.lg }} />
							</DeleteButton>
						)}
						{showCloseButton && (
							<CloseButton type="button" onClick={onClose} aria-label="Close">
								<CloseIcon sx={{ fontSize: iconSizes.lg }} />
							</CloseButton>
						)}
					</ModalHeaderActions>
				</ModalHeader>
				<ModalBody $hasFooter={Boolean(footer)}>{children}</ModalBody>
				{footer && <ModalFooter>{footer}</ModalFooter>}
			</ModalContainer>
		</ModalBackdrop>
	)
}

export default Modal
